import React from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import useQueryRMSCommonDataHook from '@fd/customHooks/useQueryRMSCommonDataHook';
import { SalesChannelType } from '@fd/customHooks/useQueryRMSSalesChannels';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { flagService } from 'services';

import { permissionsSelector } from '../../../selectors/permissions.selector';
import SettingsList, { SettingsMenuItem } from '../../../ui/List/SettingsList';
import { getStoreFrontOperatingConfigurations } from '../rms.services';
import { getSalesChannelMenuItems } from '../utils/getSalesChannelMenuItems';

const getPermissionsSelector = permissionsSelector.hasPermissionFactory(['FlipdishStaff']);

type POSChannelSettingsListProps = {
  baseUrl: string;
  salesChannelType: SalesChannelType;
};

const POSChannelSettingsList = (props: POSChannelSettingsListProps) => {
  const { baseUrl, salesChannelType } = props;
  const appId = useAppSelector((state) => state.currentApp.AppId) ?? '';
  const isFlipdishStaff = useAppSelector((state) => getPermissionsSelector(state));
  const { propertyId, channelId } = useParams<{ channelId: string; propertyId: string }>();
  const { data: rmsCommonData, isLoading: isLoadingRMSCommonData } =
    useQueryRMSCommonDataHook(appId);
  const isShowCollectionSettingsAtPropertyLevelOn = useAppSelector((state) =>
    flagService.isFlagOn(state, 'showCollectionSettingsAtPropertyLevel')
  );


  const { data: storefrontInformation } = useQuery({
    queryKey: ['getLocationId', baseUrl, salesChannelType, propertyId, channelId],
    queryFn: async () => {
      const storeFront = rmsCommonData?.associations?.orgHierarchy.properties
        .find((property) => property.code === propertyId)
        ?.storefronts.find((storefront) => storefront.code === channelId);
      if (!storeFront) {
        return {
          isUsingCreditCardTerminals: false,
          locationId: undefined,
          orderIngestType: undefined,
          orderIngestRefPriceDispatchType: undefined,
        };
      }

      const result = await getStoreFrontOperatingConfigurations(
        storeFront.franchisorId,
        storeFront.franchiseeId,
        storeFront.propertyId,
        storeFront.id
      );
      const locationId = result.data?.locationId;
      return {
        isUsingCreditCardTerminals: locationId?.length > 0,
        locationId,
        orderIngestType: storeFront.orderIngestType,
        orderIngestRefPriceDispatchType: storeFront.orderIngestRefPriceDispatchType,
      };
    },
    enabled:
      isFlipdishStaff &&
      (salesChannelType === SalesChannelType.POS ||
        salesChannelType === SalesChannelType.JUST_EATS) &&
      !isLoadingRMSCommonData &&
      !!rmsCommonData?.associations,
  });

  const menuItems = getSalesChannelMenuItems({
    appId,
    baseUrl,
    salesChannelType,
    storefrontInformation,
    propertyId,
    isShowCollectionSettingsAtPropertyLevelOn,
  });

  const shouldShowAsLast = (item: SettingsMenuItem, index: number) => {
    // For POS channels, the last item should be the last one in the array
    if (salesChannelType === SalesChannelType.POS) {
      return index === menuItems.length - 1;
    }

    // For WEB_APP and MOBILE_APP, the last item should be their respective configuration
    if (salesChannelType === SalesChannelType.WEB_APP && item.title === 'Web_App_Configuration') {
      return true;
    }

    if (
      salesChannelType === SalesChannelType.MOBILE_APP &&
      item.title === 'Mobile_App_Configuration'
    ) {
      return true;
    }

    // For other channel types, the last item should be the last one in the array
    return index === menuItems.length - 1;
  };

  return <SettingsList menuItems={menuItems} shouldShowAsLast={shouldShowAsLast} />;
};

export default POSChannelSettingsList;
